import React, { type ComponentProps, Suspense, lazy } from 'react'
import {
  LayoutBand,
  colors,
  Button,
  Paragraph,
  MoreLess,
  useOverlay,
  Separator,
  Row,
  WhiteTheme,
  Alert,
  Grid,
} from '@fs/zion-ui'
import { Trans } from 'react-i18next/icu.macro'
import { useTranslation } from 'react-i18next'
import type { BasePerson, PersonId, TreeType } from '@fs/zion-tree-types'
import { Person, ContentHistory } from '@fs/zion-icon'
import { Link, useQueryParams } from '@fs/zion-router'
import Attribution from '@fs/zion-attribution'
import useTombstonedData from '../../../data-service/useTombstonedData'
import Banner from './Banner'
import PreWrapSpan from '../../../common/PreWrapSpan'

const RestorePersonDialog = lazy(() => import('./RestorePersonDialog'))

const livingLink = 'https://familysearch.org/help/helpcenter/article/living-and-confidential-people-in-family-tree'
const confidentialLink =
  'https://www.familysearch.org/help/helpcenter/article/who-can-see-the-profiles-of-confidential-people-in-family-tree'

type ConfidentialOrLivingDescriptionProps = {
  isConfidential?: boolean
  treeType?: TreeType
}

function ConfidentialOrLivingDescription({
  isConfidential,
  treeType,
}: ConfidentialOrLivingDescriptionProps): JSX.Element {
  return (
    <Trans
      i18nKey="tree-person.living-confidential-banner.description"
      values={{ bannerType: isConfidential ? 'confidential' : 'living', treeType }}
      components={{
        L: <Link external target="_blank" to={isConfidential ? confidentialLink : livingLink} />,
      }}
    />
  )
}

type DeletedBannerProps = {
  person: Partial<BasePerson>
}

function DeletedBanner({ person }: DeletedBannerProps): JSX.Element | null {
  const [t] = useTranslation()
  const restorePersonOverlay = useOverlay()
  const { tombstonedData } = useTombstonedData(person)

  const { contributor, survivingPerson, type, deletedPerson } = tombstonedData || {}
  const deletedByMerge = type === 'DELETED_BY_PERSON_MERGE'

  if (!tombstonedData) return null
  const url = `/changelog/merge-analysis/${survivingPerson?.id}/${deletedPerson?.id}`
  const restorePersonDataTestId = 'restore-person'
  const action = deletedByMerge ? (
    <Row>
      <Button data-testid="view-merge-details" to={url}>
        {t('tree-person.view-merge-details.action')}
      </Button>
      <Button data-testid={restorePersonDataTestId} onClick={restorePersonOverlay.handleClick}>
        {t('tree-person.restore-person.action')}
      </Button>
    </Row>
  ) : (
    <Button data-testid={restorePersonDataTestId} onClick={restorePersonOverlay.handleClick}>
      {t('tree-person.restore-person.action')}
    </Button>
  )

  return (
    <>
      <Banner
        initiallyOpen
        Icon={ContentHistory}
        title={t('tree-person.deleted-person.header')}
        message={
          <Grid gutters="xxs">
            {deletedByMerge && (
              <WhiteTheme>
                <Paragraph size="sm">
                  <Trans
                    i18nKey="tree-person.deleted-person.description"
                    values={{ name: survivingPerson?.name }}
                    components={{
                      L: <Link to={`/details/${survivingPerson?.id}`} />,
                    }}
                  />
                </Paragraph>
              </WhiteTheme>
            )}
            {tombstonedData.justification && (
              <>
                <Paragraph secondary size="xs">
                  {deletedByMerge
                    ? t('tree-person.reason-was-merged.label')
                    : t('tree-person.reason-was-deleted.label')}
                </Paragraph>
                <MoreLess collapsedLineCount={3}>
                  <Paragraph size="sm">
                    <PreWrapSpan>{tombstonedData.justification}</PreWrapSpan>
                  </Paragraph>
                </MoreLess>
                <Separator size="nano" />
              </>
            )}
          </Grid>
        }
        metaText={<Attribution contributor={contributor} />}
        action={action}
      />
      <Suspense fallback="">
        {(restorePersonOverlay.isOpen || restorePersonOverlay.transitioning) && (
          <RestorePersonDialog overlay={restorePersonOverlay} tombstonedData={tombstonedData} focusPerson={person} />
        )}
      </Suspense>
    </>
  )
}

function ConfidentialOrLivingBanner({
  isConfidential,
  treeType,
}: ComponentProps<typeof ConfidentialOrLivingDescription>): JSX.Element {
  return (
    <Alert outlined>
      <span data-testid="confidential-living-description">
        <ConfidentialOrLivingDescription isConfidential={isConfidential} treeType={treeType} />
      </span>
    </Alert>
  )
}

export function LivingBanner({
  treeType,
}: Pick<ComponentProps<typeof ConfidentialOrLivingBanner>, 'treeType'>): JSX.Element {
  return <ConfidentialOrLivingBanner treeType={treeType} />
}

export function ConfidentialBanner({
  treeType,
}: Pick<ComponentProps<typeof ConfidentialOrLivingBanner>, 'treeType'>): JSX.Element {
  return <ConfidentialOrLivingBanner isConfidential treeType={treeType} />
}

export function ReadOnlyBanner(): JSX.Element {
  const link = 'https://www.familysearch.org/help/helpcenter/article/what-are-read-only-records-in-family-tree'

  return (
    <Alert outlined>
      <span>
        <Trans
          i18nKey="tree-person.read-only-banner.description"
          components={{
            L: <Link external target="_blank" to={link} rel="noopener noreferrer" />,
          }}
        />
      </span>
    </Alert>
  )
}

export function HiddenBanner(): JSX.Element {
  return (
    <Alert outlined>
      <span>
        <Trans i18nKey="tree-person.hidden-banner.description" />
      </span>
    </Alert>
  )
}

function NotFoundBanner(): JSX.Element {
  const [t] = useTranslation()
  return (
    <Banner
      Icon={Person}
      title={t('tree-person.not-found.header')}
      message={<Paragraph size="sm">{t('tree-person.not-found.description')}</Paragraph>}
    />
  )
}

type CombinedBannerProps = {
  oldPid: PersonId
  newPid: PersonId
}

function CombinedBanner({ oldPid, newPid }: CombinedBannerProps): JSX.Element {
  const [t] = useTranslation()
  return (
    <Banner
      Icon={Person}
      title={t('tree-person.combined.header')}
      message={<Paragraph size="sm">{t('tree-person.combined.description', { oldPid, newPid })}</Paragraph>}
    />
  )
}

type BannersProps = {
  person?: Partial<BasePerson>
  notFound?: boolean
  maxWidth?: 'nano' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
}

export default function Banners({ person, notFound, maxWidth = 'xxs' }: BannersProps): JSX.Element {
  const {
    query: { innerId },
  } = useQueryParams()

  return (
    <LayoutBand maxWidth={maxWidth} color={colors.gray80}>
      {person?.id && innerId && <CombinedBanner newPid={person?.id} oldPid={innerId} />}
      {person?.deleted && <DeletedBanner person={person} />}
      {notFound && <NotFoundBanner />}
    </LayoutBand>
  )
}
